import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowTopRightOnSquareIcon,
  ArrowRightIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useLoginStatus } from "../../hooks/useLoginStatus";
import useTranslation from "../../hooks/useTranslation";

type StaffUserRow = {
  id: string;
  name: string;
  email: string;
  createdAt: string;
  lastActiveAt: string;
  activeToday: boolean;
  sessions7d: number;
  generatedTexts7d: number;
  vocabularyItems7d: number;
  tokens7d: number;
  spend7dUsd: number;
  revenue7dUsd: number;
  languageSpace: string;
  status: "active" | "trial" | "suspended";
  isTestGenerated?: boolean;
};

type UserEventRow = {
  userId: string;
  at: string;
  type: string;
  feature: string;
  model: string;
  tokens: number;
  costUsd: number;
  status: string;
};

type PricingRow = {
  model: string;
  input: string;
  cachedInput: string;
  output: string;
  notes: string;
};

type OperationRow = {
  at: string;
  status: string;
  area: string;
  detail: string;
};

type LanguageCountRow = {
  languageSpace: string;
  users: number;
};

const staffUsers: StaffUserRow[] = [
  {
    id: "U-1042",
    name: "Moro",
    email: "moro@example.com",
    createdAt: "2026-06-18T08:35:00+02:00",
    lastActiveAt: "2026-07-30T11:41:00+02:00",
    activeToday: true,
    sessions7d: 18,
    generatedTexts7d: 6,
    vocabularyItems7d: 17,
    tokens7d: 218_430,
    spend7dUsd: 3.62,
    revenue7dUsd: 12.00,
    languageSpace: "ITALIAN",
    status: "active",
  },
  {
    id: "U-1077",
    name: "Amina",
    email: "amina@example.com",
    createdAt: "2026-06-24T13:15:00+02:00",
    lastActiveAt: "2026-07-29T19:08:00+02:00",
    activeToday: false,
    sessions7d: 11,
    generatedTexts7d: 2,
    vocabularyItems7d: 14,
    tokens7d: 144_120,
    spend7dUsd: 2.41,
    revenue7dUsd: 12.00,
    languageSpace: "FRENCH",
    status: "trial",
  },
  {
    id: "U-1108",
    name: "Jonas",
    email: "jonas@example.com",
    createdAt: "2026-05-03T09:10:00+02:00",
    lastActiveAt: "2026-07-28T16:02:00+02:00",
    activeToday: false,
    sessions7d: 4,
    generatedTexts7d: 1,
    vocabularyItems7d: 4,
    tokens7d: 40_800,
    spend7dUsd: 0.88,
    revenue7dUsd: 0.00,
    languageSpace: "GERMAN",
    status: "active",
  },
  {
    id: "U-1130",
    name: "Test Bot 01",
    email: "test-bot-01@example.com",
    createdAt: "2026-07-29T14:10:00+02:00",
    lastActiveAt: "2026-07-30T08:22:00+02:00",
    activeToday: true,
    sessions7d: 9,
    generatedTexts7d: 1,
    vocabularyItems7d: 9,
    tokens7d: 61_500,
    spend7dUsd: 1.14,
    revenue7dUsd: 0.00,
    languageSpace: "ENGLISH",
    status: "trial",
    isTestGenerated: true,
  },
  {
    id: "U-1131",
    name: "Test Bot 02",
    email: "test-bot-02@example.com",
    createdAt: "2026-07-29T14:12:00+02:00",
    lastActiveAt: "2026-07-30T08:25:00+02:00",
    activeToday: false,
    sessions7d: 6,
    generatedTexts7d: 0,
    vocabularyItems7d: 6,
    tokens7d: 28_300,
    spend7dUsd: 0.52,
    revenue7dUsd: 0.00,
    languageSpace: "ITALIAN",
    status: "trial",
    isTestGenerated: true,
  },
  {
    id: "U-1132",
    name: "Lea",
    email: "lea@example.com",
    createdAt: "2026-07-18T09:55:00+02:00",
    lastActiveAt: "2026-07-30T09:04:00+02:00",
    activeToday: true,
    sessions7d: 14,
    generatedTexts7d: 3,
    vocabularyItems7d: 11,
    tokens7d: 97_240,
    spend7dUsd: 1.84,
    revenue7dUsd: 12.00,
    languageSpace: "FRENCH",
    status: "active",
  },
];

const eventRows: UserEventRow[] = [
  {
    userId: "U-1077",
    at: "2026-07-30T11:40:10+02:00",
    type: "vocabulary translation",
    feature: "Vocabulary capture",
    model: "gpt-4o-mini",
    tokens: 13_420,
    costUsd: 0.0107,
    status: "success",
  },
  {
    userId: "U-1077",
    at: "2026-07-30T11:24:04+02:00",
    type: "speaking turn",
    feature: "Roleplay",
    model: "gpt-4o-mini",
    tokens: 3_610,
    costUsd: 0.0030,
    status: "success",
  },
  {
    userId: "U-1042",
    at: "2026-07-30T10:58:21+02:00",
    type: "generated text",
    feature: "Reeprise",
    model: "gpt-4o",
    tokens: 19_880,
    costUsd: 0.0331,
    status: "retry",
  },
  {
    userId: "U-1108",
    at: "2026-07-30T10:17:09+02:00",
    type: "rabbit hole topic",
    feature: "Rabbit Hole",
    model: "gpt-5.6-luna",
    tokens: 8_240,
    costUsd: 0.0101,
    status: "success",
  },
  {
    userId: "U-1130",
    at: "2026-07-30T08:22:00+02:00",
    type: "generated text",
    feature: "Reeprise",
    model: "gpt-4o-mini",
    tokens: 10_120,
    costUsd: 0.0084,
    status: "success",
  },
  {
    userId: "U-1131",
    at: "2026-07-30T08:25:00+02:00",
    type: "vocabulary translation",
    feature: "Vocabulary capture",
    model: "gpt-4o-mini",
    tokens: 5_840,
    costUsd: 0.0046,
    status: "success",
  },
];

const pricingRows: PricingRow[] = [
  { model: "gpt-5.6-sol", input: "$5.00", cachedInput: "$0.50", output: "$30.00", notes: "High-complexity generation and synthesis" },
  { model: "gpt-5.6-terra", input: "$2.50", cachedInput: "$0.25", output: "$15.00", notes: "Balanced quality / cost" },
  { model: "gpt-5.6-luna", input: "$1.00", cachedInput: "$0.10", output: "$6.00", notes: "Cost-sensitive bulk work" },
  { model: "gpt-4o", input: "$2.50", cachedInput: "$1.25", output: "$10.00", notes: "General-purpose text and multimodal flows" },
  { model: "gpt-4o-mini", input: "$0.15", cachedInput: "$0.075", output: "$0.60", notes: "Focused extraction and low-cost helpers" },
];

const opsRows: OperationRow[] = [
  { at: "2026-07-30T11:52:00+02:00", status: "healthy", area: "Worker queue", detail: "All job queues clear. No stuck generation tasks." },
  { at: "2026-07-30T11:44:00+02:00", status: "warning", area: "Billing ledger", detail: "One request row missing cached-input tokens; review ingestion." },
  { at: "2026-07-30T11:36:00+02:00", status: "healthy", area: "Model spend", detail: "gpt-4o-mini remains the largest volume model by request count." },
  { at: "2026-07-30T11:12:00+02:00", status: "warning", area: "Retry rate", detail: "Roleplay retries exceeded the 7-day rolling threshold." },
];

const EVENT_LABEL_BY_TYPE: Record<string, string> = {
  "generated text": "staff_console_event_generated_text",
  "vocabulary translation": "staff_console_event_vocabulary",
  "speaking turn": "staff_console_event_roleplay",
  "rabbit hole topic": "staff_console_event_rabbit_hole",
};

function formatDateTime(iso: string) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(iso));
}

function formatNumber(value: number) {
  return new Intl.NumberFormat(undefined).format(value);
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(value);
}

function MetricRow({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <tr className="border-b border-slate-200 last:border-b-0">
      <th className="px-0 py-2 text-left text-xs font-medium normal-case tracking-[0.08em] text-slate-600">
        {label}
      </th>
      <td className="px-0 py-2 text-right text-sm font-light tabular-nums text-slate-950">
        {value}
      </td>
    </tr>
  );
}

function Badge({ tone, children }: { tone: "good" | "warn" | "neutral"; children: string }) {
  const className =
    tone === "good"
      ? "bg-emerald-50 text-emerald-800"
      : tone === "warn"
        ? "bg-amber-50 text-amber-800"
        : "bg-slate-50 text-slate-700";
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${className}`}>
      {children}
    </span>
  );
}

function TestGeneratedBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800">
      {label}
    </span>
  );
}

function SectionCard({
  title,
  eyebrow,
  children,
}: {
  title: string;
  eyebrow: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-4">
      <div className="space-y-1">
        <p className="text-xs font-semibold normal-case tracking-[0.08em] text-slate-600">
          {eyebrow}
        </p>
        <h2 className="text-xl font-semibold tracking-tight text-slate-950">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function StaffConsolePage() {
  const loginStatus = useLoginStatus();
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const [filter, setFilter] = useState("");
  const isStaff =
    loginStatus.data?.state === "logged in" && loginStatus.data.is_staff;

  useEffect(() => {
    if (loginStatus.isSuccess && !isStaff) {
      navigate({ to: "/app/product_logbook", replace: true });
    }
  }, [isStaff, loginStatus.isSuccess, navigate]);

  const filteredUsers = useMemo(() => {
    const term = filter.trim().toLowerCase();
    if (!term) return staffUsers;
    return staffUsers.filter((user) =>
      [user.id, user.name, user.email, user.languageSpace, user.status]
        .join(" ")
        .toLowerCase()
        .includes(term),
    );
  }, [filter]);

  const selectedUser = filteredUsers[0] ?? staffUsers[0];
  const languageCounts = useMemo<LanguageCountRow[]>(() => {
    const counts = new Map<string, number>();
    for (const user of staffUsers) {
      counts.set(user.languageSpace, (counts.get(user.languageSpace) ?? 0) + 1);
    }
    return [...counts.entries()]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([languageSpace, users]) => ({ languageSpace, users }));
  }, []);
  const selectedUserEvents = useMemo(
    () => eventRows.filter((row) => row.userId === selectedUser?.id),
    [selectedUser?.id],
  );
  const selectedFeatureBreakdown = useMemo(() => {
    const rows = new Map<
      string,
      { feature: string; sessions: number; tokens: number; costUsd: number }
    >();
    for (const row of selectedUserEvents) {
      const current =
        rows.get(row.feature) ??
        { feature: row.feature, sessions: 0, tokens: 0, costUsd: 0 };
      current.sessions += 1;
      current.tokens += row.tokens;
      current.costUsd += row.costUsd;
      rows.set(row.feature, current);
    }
    return [...rows.values()];
  }, [selectedUserEvents]);
  const selectedGeneratedTexts = selectedUser?.generatedTexts7d ?? 0;
  const selectedVocabularyItems = selectedUser?.vocabularyItems7d ?? 0;

  if (!loginStatus.isSuccess || !isStaff) {
    return <div className="w-full px-4 py-6 sm:px-8 lg:px-12" />;
  }

  return (
    <div className="w-full px-4 py-6 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-7xl space-y-8">
        <main className="space-y-8">
          <header className="space-y-2">
            <h1 className="text-3xl font-semibold tracking-tight text-slate-950">
              {translate("staff_console_title")}
            </h1>
            <p className="max-w-3xl text-sm leading-relaxed text-slate-700">
              {translate("staff_console_description")}
            </p>
          </header>
          <section id="dashboard" className="space-y-4 p-2 sm:p-0">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <a
                href="/admin/"
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                {translate("staff_console_open_django_admin")}
              </a>
            </div>
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
              <table className="w-full text-sm">
                <tbody>
                  <MetricRow label={translate("staff_console_metric_sessions")} value={formatNumber(selectedUser.sessions7d)} />
                  <MetricRow label={translate("staff_console_metric_tokens")} value={formatNumber(selectedUser.tokens7d)} />
                  <MetricRow label={translate("staff_console_metric_cost")} value={formatCurrency(selectedUser.spend7dUsd)} />
                  <MetricRow label={translate("staff_console_metric_revenue")} value={formatCurrency(selectedUser.revenue7dUsd)} />
                  <MetricRow label={translate("staff_console_user_detail_generated_texts")} value={formatNumber(selectedGeneratedTexts)} />
                  <MetricRow label={translate("staff_console_user_detail_vocabulary_items")} value={formatNumber(selectedVocabularyItems)} />
                </tbody>
              </table>
              <table className="w-full text-sm">
                <tbody>
                  <MetricRow label={translate("staff_console_user_detail_primary_space")} value={selectedUser.languageSpace} />
                  <MetricRow label={translate("staff_console_user_detail_last_seen")} value={formatDateTime(selectedUser.lastActiveAt)} />
                  <MetricRow label={translate("staff_console_user_detail_active")} value={selectedUser.activeToday ? "yes" : "no"} />
                  <MetricRow label={translate("staff_console_metric_margin")} value={formatCurrency(selectedUser.revenue7dUsd - selectedUser.spend7dUsd)} />
                  <MetricRow label={translate("staff_console_metric_failures")} value={String(selectedUserEvents.filter((row) => row.status !== "success").length)} />
                  <MetricRow label={translate("staff_console_metric_retry_rate")} value={`${Math.round((selectedUserEvents.filter((row) => row.status === "retry").length / Math.max(selectedUserEvents.length, 1)) * 100)}%`} />
                </tbody>
              </table>
            </div>
          </section>

          <SectionCard
            eyebrow={translate("staff_console_users_eyebrow")}
            title={translate("staff_console_users_title")}
          >
            <div className="space-y-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="relative max-w-md flex-1">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    value={filter}
                    onChange={(event) => setFilter(event.target.value)}
                    className="w-full border border-slate-300 bg-white px-10 py-3 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-400"
                    placeholder={translate("staff_console_users_search")}
                  />
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <FunnelIcon className="h-4 w-4" />
                  {translate("staff_console_users_count")} {filteredUsers.length}
                </div>
              </div>
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="text-xs normal-case tracking-[0.08em] text-slate-600">
                    <tr className="border-b border-slate-300">
                      <th className="px-3 py-3 font-medium">{translate("staff_console_col_user")}</th>
                      <th className="px-3 py-3 font-medium">{translate("staff_console_col_last_active")}</th>
                      <th className="px-3 py-3 font-medium">{translate("staff_console_col_sessions")}</th>
                      <th className="px-3 py-3 font-medium">{translate("staff_console_col_generated_texts")}</th>
                      <th className="px-3 py-3 font-medium">{translate("staff_console_col_vocabulary_items")}</th>
                      <th className="px-3 py-3 font-medium">{translate("staff_console_col_tokens")}</th>
                      <th className="px-3 py-3 font-medium">{translate("staff_console_col_spend")}</th>
                      <th className="px-3 py-3 font-medium">{translate("staff_console_col_space")}</th>
                      <th className="px-3 py-3 font-medium">{translate("staff_console_col_status")}</th>
                      <th className="px-3 py-3 font-medium">{translate("staff_console_col_open")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr
                        key={user.id}
                        className={`border-b border-slate-200 transition ${
                          user.id === selectedUser?.id ? "bg-slate-50" : ""
                        }`}
                      >
                        <td className="px-3 py-4">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-950">{user.name}</p>
                              {user.id === selectedUser?.id ? (
                                <Badge tone="neutral">
                                  {translate("staff_console_user_detail_active")}
                                </Badge>
                              ) : null}
                            </div>
                            <p className="text-xs text-slate-600">{user.email}</p>
                            {user.isTestGenerated ? (
                              <TestGeneratedBadge
                                label={translate("staff_console_test_generated")}
                              />
                            ) : null}
                          </div>
                        </td>
                        <td className="px-3 py-4 text-slate-700">{formatDateTime(user.lastActiveAt)}</td>
                        <td className="px-3 py-4 text-slate-700">{formatNumber(user.sessions7d)}</td>
                        <td className="px-3 py-4 text-slate-700">{formatNumber(user.generatedTexts7d)}</td>
                        <td className="px-3 py-4 text-slate-700">{formatNumber(user.vocabularyItems7d)}</td>
                        <td className="px-3 py-4 text-slate-700">{formatNumber(user.tokens7d)}</td>
                        <td className="px-3 py-4 text-slate-700">{formatCurrency(user.spend7dUsd)}</td>
                        <td className="px-3 py-4 text-slate-700">{user.languageSpace}</td>
                        <td className="px-3 py-4">
                          <Badge tone={user.status === "active" ? "good" : user.status === "trial" ? "neutral" : "warn"}>
                            {user.status}
                          </Badge>
                        </td>
                        <td className="px-3 py-4">
                          <button
                            type="button"
                            onClick={() => navigate({ to: `/app/staff_console_user/${user.id}` })}
                            className="inline-flex items-center gap-2 border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                            aria-pressed={user.id === selectedUser?.id}
                          >
                            <UserCircleIcon className="h-4 w-4" />
                            {translate("staff_console_open_user")}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            eyebrow={translate("staff_console_languages_eyebrow")}
            title={translate("staff_console_languages_title")}
          >
            <div>
              <table className="w-full text-sm">
                <thead className="text-xs normal-case tracking-[0.08em] text-slate-600">
                  <tr className="border-b border-slate-300">
                    <th className="px-0 py-2 text-left font-medium">
                      {translate("staff_console_languages_eyebrow")}
                    </th>
                    <th className="px-0 py-2 text-right font-medium">
                      {translate("staff_console_languages_users")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {languageCounts.map((row) => (
                    <tr
                      key={row.languageSpace}
                      data-testid={`language-count-${row.languageSpace.toLowerCase()}`}
                      className="border-b border-slate-200 last:border-b-0"
                    >
                      <td className="px-0 py-3 font-light text-slate-950">
                        {row.languageSpace}
                      </td>
                      <td className="px-0 py-3 text-right font-light tabular-nums text-slate-950">
                        {formatNumber(row.users)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>

          <SectionCard
            eyebrow={translate("staff_console_user_detail_eyebrow")}
            title={translate("staff_console_user_detail_title")}
          >
            <div id="user-detail" className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_16rem]">
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-light text-slate-950">{selectedUser.name}</span>
                  <span className="font-light text-slate-500">{selectedUser.email}</span>
                  {selectedUser.isTestGenerated ? (
                    <TestGeneratedBadge label={translate("staff_console_test_generated")} />
                  ) : null}
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    <MetricRow label={translate("staff_console_user_detail_active")} value={selectedUser.activeToday ? "yes" : "no"} />
                    <MetricRow label={translate("staff_console_user_detail_last_seen")} value={formatDateTime(selectedUser.lastActiveAt)} />
                    <MetricRow label={translate("staff_console_user_detail_lifetime_tokens")} value={formatNumber(selectedUser.tokens7d)} />
                    <MetricRow label={translate("staff_console_user_detail_lifetime_spend")} value={formatCurrency(selectedUser.spend7dUsd)} />
                    <MetricRow label={translate("staff_console_user_detail_sessions")} value={formatNumber(selectedUser.sessions7d)} />
                    <MetricRow label={translate("staff_console_user_detail_primary_space")} value={selectedUser.languageSpace} />
                    <MetricRow label={translate("staff_console_user_detail_generated_texts")} value={formatNumber(selectedGeneratedTexts)} />
                    <MetricRow label={translate("staff_console_user_detail_vocabulary_items")} value={formatNumber(selectedVocabularyItems)} />
                  </tbody>
                </table>
                <table className="w-full text-sm">
                  <thead className="text-xs normal-case tracking-[0.08em] text-slate-600">
                    <tr className="border-b border-slate-200">
                      <th className="px-0 py-2 text-left font-medium">{translate("staff_console_user_detail_timeline")}</th>
                      <th className="px-0 py-2 text-right font-medium">{translate("staff_console_col_tokens")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedUserEvents.map((row) => (
                      <tr key={`${row.at}-${row.type}`} className="border-b border-slate-200 last:border-b-0">
                        <td className="px-0 py-3 font-light text-slate-950">
                          {formatDateTime(row.at)} {translate(EVENT_LABEL_BY_TYPE[row.type] ?? "")}
                        </td>
                      <td className="px-0 py-3 text-right font-light tabular-nums text-slate-700">
                          {formatNumber(row.tokens)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="space-y-4">
                <p className="text-xs font-medium normal-case tracking-[0.08em] text-slate-600">
                  {translate("staff_console_user_detail_notes")}
                </p>
                <ul className="space-y-2 text-sm font-light leading-relaxed text-slate-700">
                  <li>{translate("staff_console_user_detail_note_1")}</li>
                  <li>{translate("staff_console_user_detail_note_2")}</li>
                  <li>{translate("staff_console_user_detail_note_3")}</li>
                </ul>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            eyebrow={translate("staff_console_ledger_eyebrow")}
            title={translate("staff_console_ledger_title")}
          >
            <div id="ledger">
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="text-xs normal-case tracking-[0.08em] text-slate-600">
                    <tr className="border-b border-slate-200">
                      <th className="px-3 py-3 font-medium">{translate("staff_console_col_time")}</th>
                      <th className="px-3 py-3 font-medium">{translate("staff_console_col_feature")}</th>
                      <th className="px-3 py-3 font-medium">{translate("staff_console_col_model")}</th>
                      <th className="px-3 py-3 font-medium">{translate("staff_console_col_ledger_tokens")}</th>
                      <th className="px-3 py-3 font-medium">{translate("staff_console_col_ledger_cost")}</th>
                      <th className="px-3 py-3 font-medium">{translate("staff_console_col_ledger_status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedUserEvents.map((row) => (
                      <tr key={`${row.at}-${row.feature}`} className="border-b border-slate-200">
                        <td className="px-3 py-4 text-slate-700">{formatDateTime(row.at)}</td>
                        <td className="px-3 py-4 text-slate-950">{row.feature}</td>
                        <td className="px-3 py-4 text-slate-700">{row.model}</td>
                        <td className="px-3 py-4 text-slate-700">{formatNumber(row.tokens)}</td>
                        <td className="px-3 py-4 text-slate-700">{formatCurrency(row.costUsd)}</td>
                        <td className="px-3 py-4">
                          <Badge tone={row.status === "success" ? "good" : "warn"}>{row.status}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            eyebrow={translate("staff_console_feature_breakdown_eyebrow")}
            title={translate("staff_console_feature_breakdown_title")}
          >
            <div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="text-xs normal-case tracking-[0.08em] text-slate-600">
                    <tr className="border-b border-slate-200">
                      <th className="px-3 py-3 font-medium">{translate("staff_console_col_feature")}</th>
                      <th className="px-3 py-3 font-medium">{translate("staff_console_col_sessions")}</th>
                      <th className="px-3 py-3 font-medium">{translate("staff_console_col_tokens")}</th>
                      <th className="px-3 py-3 font-medium">{translate("staff_console_col_ledger_cost")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedFeatureBreakdown.map((row) => (
                      <tr key={row.feature} className="border-b border-slate-200">
                        <td className="px-3 py-4 font-medium text-slate-950">{row.feature}</td>
                        <td className="px-3 py-4 text-slate-700">{formatNumber(row.sessions)}</td>
                        <td className="px-3 py-4 text-slate-700">{formatNumber(row.tokens)}</td>
                        <td className="px-3 py-4 text-slate-700">{formatCurrency(row.costUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            eyebrow={translate("staff_console_pricing_eyebrow")}
            title={translate("staff_console_pricing_title")}
          >
            <div id="pricing">
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="text-xs normal-case tracking-[0.08em] text-slate-600">
                    <tr className="border-b border-slate-200">
                      <th className="px-3 py-3 font-medium">{translate("staff_console_col_price_model")}</th>
                      <th className="px-3 py-3 font-medium">{translate("staff_console_col_price_input")}</th>
                      <th className="px-3 py-3 font-medium">{translate("staff_console_col_price_cached")}</th>
                      <th className="px-3 py-3 font-medium">{translate("staff_console_col_price_output")}</th>
                      <th className="px-3 py-3 font-medium">{translate("staff_console_col_price_notes")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pricingRows.map((row) => (
                      <tr key={row.model} className="border-b border-slate-200">
                        <td className="px-3 py-4 font-medium text-slate-950">{row.model}</td>
                        <td className="px-3 py-4 text-slate-700">{row.input}</td>
                        <td className="px-3 py-4 text-slate-700">{row.cachedInput}</td>
                        <td className="px-3 py-4 text-slate-700">{row.output}</td>
                        <td className="px-3 py-4 text-slate-700">{row.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            eyebrow={translate("staff_console_operations_eyebrow")}
            title={translate("staff_console_operations_title")}
          >
            <div id="operations" className="grid gap-4 lg:grid-cols-[1fr_18rem]">
              <div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="text-xs normal-case tracking-[0.08em] text-slate-600">
                      <tr className="border-b border-slate-200">
                        <th className="px-3 py-3 font-medium">{translate("staff_console_col_ops_time")}</th>
                        <th className="px-3 py-3 font-medium">{translate("staff_console_col_ops_area")}</th>
                        <th className="px-3 py-3 font-medium">{translate("staff_console_col_ops_detail")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {opsRows.map((row) => (
                      <tr key={`${row.at}-${row.area}`} className="border-b border-slate-200">
                          <td className="px-3 py-4 text-slate-700">{formatDateTime(row.at)}</td>
                          <td className="px-3 py-4">
                            <Badge tone={row.status === "healthy" ? "good" : "warn"}>{row.area}</Badge>
                          </td>
                          <td className="px-3 py-4 text-slate-700">{row.detail}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold normal-case text-slate-900">{translate("staff_console_operations_sidebar_title")}</p>
                <ul className="mt-3 space-y-2 text-sm leading-relaxed text-slate-700">
                  <li>{translate("staff_console_operations_sidebar_1")}</li>
                  <li>{translate("staff_console_operations_sidebar_2")}</li>
                  <li>{translate("staff_console_operations_sidebar_3")}</li>
                </ul>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            eyebrow={translate("staff_voice_review_eyebrow")}
            title={translate("staff_voice_review_title")}
          >
            <div className="space-y-4 border border-slate-200 p-4">
              <p className="text-sm leading-relaxed text-slate-700">
                {translate("staff_voice_review_description")}
              </p>
              <Link
                to="/app/staff_voice_review"
                className="inline-flex items-center gap-2 border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
              >
                <ArrowRightIcon className="h-4 w-4" />
                {translate("staff_voice_review_open")}
              </Link>
            </div>
          </SectionCard>
        </main>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/app/staff_console")({
  component: StaffConsolePage,
});
